import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import {
  buttonWords,
  colour,
  drawOrder,
  onScreen,
  textIn,
  withPreviewText,
  type ElementKind,
  type LayoutCanvas,
  type Screen,
  type LayoutDocument,
  type LayoutElement,
} from './types'
import { SAMPLE_QR_PATH, SAMPLE_QR_SIZE } from './sampleQR'
import { ambientOn, canvasPaint, elementInk, groundInk, logoSource } from './look'
import { STANDARD_FONT, fontOf, fontStack, useFontLoaded, usePreloadDesignFonts } from './fonts'
import { FLIP_STEPS, enterOrder, entranceShown, screenChangeOf, type ScreenFlip } from './counterMotion'
import qmeterLogo from '../assets/qmeter-logo.png'
import { autoFit, DEFAULT_MIN_SCALE, fitsRows, fitText, minScaleOf, MIN_FIT_SIZE, type Fitted, type Measure } from './fit'
import { gridColumn, gridRows } from './waitingGrid'
import { pillSize } from './pill'
import { formatDate } from './dateFormat'
import { ruleThickness } from './rule'
import { MediaPlayer } from './MediaPlayer'
import { Ticker } from './Ticker'
import { tickerLinesOf } from './screenBindings'
import { t, type DeskPill, type Language } from '../lib/kioskText'
import { aheadLine, deskLabel, inQueueWords, ownWord, pillWords, plainDeskLabel, validUntilWords, waitWords, wordIn, type Words } from './words'
import { SERVICE_GAP, SERVICE_ROW } from '../lib/kioskMenu'
import { kioskIcon } from './icons'
import { otherLanguages, switchLabel } from '../lib/kioskStart'
import { t as staffSay, type StaffLanguage } from '../lib/staffText'
import type { ButtonAction } from './types'

/**
 * Drawing a layout.
 *
 * One renderer, used by the hall board, the kiosk and the editor's preview. That is
 * the whole point of it: a preview drawn by a second implementation is a preview
 * that eventually lies, and the person it lies to is standing in front of a customer
 * explaining what their board will look like.
 *
 * A layout is designed against a fixed canvas — 1920×1080 for a board, 1080×1920 for
 * a kiosk — and scaled to whatever screen it lands on. Everything inside is
 * positioned in canvas units, so a design does not reflow, reorder or wrap on a
 * different panel: it is the same picture, larger or smaller. That is what a
 * customer means by "what I designed is what is on the wall".
 */

/** A design's language as one the phrase table has. */
const phraseLanguage = (l?: string): StaffLanguage => (l === 'ru' || l === 'az' ? l : 'en')

/** The language a screen draws in: the chosen one, else the organisation's first, else English. */
const screenLanguage = (b: LayoutBindings): string => b.language ?? b.languages?.[0]?.code ?? 'en'

/** What the live data looks like to an element that needs some. */
export interface LayoutBindings {
  branchName?: string
  language?: string
  now?: Date

  /** desk is the desk's name, deskNumber its number (lib/counterDisplay deskNumberOf). */
  calls?: Array<{ id: string; number: string; desk: string; deskNumber?: string }>
  /** What to say when nothing has been called: the organisation's line, empty by default. */
  callsEmptyText?: string
  /** tickets: the queue's waiting numbers, oldest first, for the grid view (waitingGrid.ts). */
  queues?: Array<{ id: string; name: string; waiting: number; tickets?: string[] }>

  services?: Array<{ id: string; prefix: string; name: string }>
  /** What one grid shows, where grids on different pages show different services.
   *  Absent, every grid shows `services`, as the editor's preview always has. */
  servicesFor?: (grid: LayoutElement) => Array<{ id: string; prefix: string; name: string }>
  onPickService?: (id: string) => void

  /** Which menu page is showing. Absent draws every page's elements at once. */
  page?: string
  /** Elements not drawn at this branch: buttons for services it does not offer,
   *  and for pages with nothing in them it can issue (lib/kioskPages.ts). */
  hidden?: ReadonlySet<string>
  /** What a button says when its designer left it to the service or page. */
  serviceLabel?: (serviceId: string) => string
  pageLabel?: (pageId: string) => string
  onButton?: (action: ButtonAction) => void

  languages?: Array<{ code: string; label: string }>
  onPickLanguage?: (code: string) => void

  ticketNumber?: string
  /** How many are in front, already counted. Undefined when the server could not
   *  count, and then nothing is said: zero means "you are next" and is only ever
   *  a real zero. */
  ticketAhead?: number
  ticketWait?: string
  /** What the customer is waiting for. Part of the ticket's facts rather than an
   *  element of its own: "3rd for the cash desk, about 8 minutes" is one sentence,
   *  and splitting it would let somebody design a slip that says how long the wait
   *  is without saying what for. */
  ticketService?: string
  ticketIssuedAt?: Date
  /** The slip's validity, already "HH:mm" on the branch's clock. Absent prints
   *  nothing: the editor's sample supplies one, and paper is the agent's. */
  ticketValidUntil?: string
  qrValue?: string

  /** A counter display's desk: its name, its state, and the service of the ticket at it. */
  deskName?: string
  deskStatus?: 'open' | 'paused' | 'closed'
  /** The desk's number alone (lib/counterDisplay deskNumberOf). */
  deskNumber?: string
  /** What the status pill says: the ticket's state while one is at the desk, else the desk's. */
  deskPill?: DeskPill
  /** The signed-in clerk's first name; the server sends it only to a design that shows it. */
  clerkName?: string
  serviceName?: string
  /** A counter display's entrances only fade (layout/counterMotion.ts). */
  reducedMotion?: boolean
  /** A counter display's flip-clock screen change while it runs (layout/CounterStage.tsx). */
  flip?: ScreenFlip
  /** Set on the number's flip card: the number it turns over from. */
  numberFlip?: NumberFlip

  /** Where an uploaded picture is fetched from. */
  assetURL?: (assetId: string) => string

  /** The playing playlist's ticker lines in the screen's language (screenBindings.tickerLines). */
  ticker?: string[]

  /** A playlist item the screen skipped because it would not load or play (lib/mediaSkips). */
  onMediaSkipped?: (assetId: string, kind: 'image' | 'video') => void

  /** Whether this is somebody designing rather than a customer reading.
   *
   *  It is what lets an element's preview text be drawn, and it is set by the
   *  editor and by the gallery's thumbnails and by nothing else. A hall board, a
   *  kiosk and the printer resolve a design against the values the visit actually
   *  carries; a sample somebody typed while drawing must never be one of them. */
  preview?: boolean
  /** A preview that runs: playlists move on and films play, as on a wall. */
  playing?: boolean

  /** The editor's canvas only: a live field's name, drawn as a chip in place of a sample value. */
  keyword?: (kind: ElementKind) => string | undefined
}

/**
 * Fit a canvas into the space available.
 *
 * Contain rather than cover: a hall board that fills the screen by cropping is a
 * hall board with a number half off the edge.
 */
export function useCanvasScale(width: number, height: number) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const measure = () => {
      const box = node.getBoundingClientRect()
      if (box.width === 0 || box.height === 0) return
      setScale(Math.min(box.width / width, box.height / height))
    }
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [width, height])

  return { ref, scale }
}

export function LayoutSurface({
  document: doc,
  bindings,
  screen,
  children,
  scale: fixed,
}: {
  document: LayoutDocument
  bindings: LayoutBindings
  screen?: Screen
  children?: ReactNode
  /** Drawn at this scale instead of fitted (the editor's 1:1 preview). */
  scale?: number
}) {
  const { ref, scale: fitted } = useCanvasScale(doc.canvas.width, doc.canvas.height)
  const scale = fixed ?? fitted
  usePreloadDesignFonts(doc)

  return (
    <div className="layout-fit" ref={ref} data-theme={doc.canvas.theme ?? undefined}>
      {/* Two boxes, and both are needed. The frame is the canvas's size after
          scaling, and it is what the centring above acts on; the canvas itself
          keeps its designed size and is scaled from its top-left corner. Centring
          the unscaled canvas instead puts half a hall board off the left edge of
          the screen, which is exactly what it looks like. */}
      <div
        className="layout-frame"
        style={{ width: doc.canvas.width * scale, height: doc.canvas.height * scale }}
      >
        <div
          className="layout-canvas"
          data-ink={groundInk(doc.canvas)}
          style={{
            width: doc.canvas.width,
            height: doc.canvas.height,
            transform: `scale(${scale})`,
            background: canvasPaint(doc.canvas, bindings.assetURL),
            fontFamily: fontStack(doc.canvas.font),
          }}
        >
          {ambientOn(doc, screen) && <Ambient />}
          <LayoutElements document={doc} bindings={bindings} screen={screen} page={bindings.page} />
          {children}
        </div>
      </div>
    </div>
  )
}

/** The elements alone, for an editor that provides its own surface. */
export function LayoutElements({
  document: doc,
  bindings,
  screen,
  page,
}: {
  document: LayoutDocument
  bindings: LayoutBindings
  screen?: Screen
  page?: string
}) {
  const shown = drawOrder(doc.elements).filter((el) => !el.hidden && onScreen(el, screen, page) && !bindings.hidden?.has(el.id))
  if (doc.kind === 'counter' && bindings.flip) return <FlipCards document={doc} shown={shown} bindings={bindings} flip={bindings.flip} />
  // A counter display's boxes carry their entrance, played by .counter-shell.
  const order = doc.kind === 'counter' ? enterOrder(shown) : null
  const change = screenChangeOf(doc.canvas)
  return (
    <>
      {shown.map((el) => (
          // lang is what makes UPPERCASE correct rather than merely capital.
          //
          // Azerbaijani has two i's, and capitalising them is not the English
          // rule: i becomes İ and ı becomes I. A browser applies the rule of the
          // element's language, and this document was served inside a page
          // declared as English — so a board reading "Əvvəlki" came out as
          // "ƏVVƏLKI", which is a spelling mistake a metre tall on a wall in
          // Baku. Found on a trial board on 17 September 2026.
          <div
            key={el.id}
            lang={screenLanguage(bindings)}
            style={order ? { ...boxStyle(el), ['--enter-i' as string]: order.get(el.id) ?? 0 } : boxStyle(el)}
            data-element={el.id}
            data-ink={elementInk(el.style?.background, doc.canvas)}
            data-motion={order ? entranceShown(change, el, bindings.reducedMotion ?? false) : undefined}
            data-number={(order && el.kind === 'ticketNumber') || undefined}
          >
            <ElementBody element={el} bindings={bindings} kind={doc.kind} canvas={doc.canvas} words={doc.words} />
          </div>
        ))}
    </>
  )
}

/**
 * A counter display's screen change as a flip clock: every element on either screen is
 * a card whose old top half folds down over the new one, top to bottom. The number
 * turns over per character instead (NumberFace). Keyed by the flip, so a new one restarts.
 */
function FlipCards({
  document: doc,
  shown,
  bindings,
  flip,
}: {
  document: LayoutDocument
  shown: LayoutElement[]
  bindings: LayoutBindings
  flip: ScreenFlip
}) {
  const from = flip.from.bindings
  const was = new Set(
    flip.numberOnly
      ? shown.filter((el) => el.kind === 'ticketNumber').map((el) => el.id)
      : doc.elements.filter((el) => !el.hidden && onScreen(el, flip.from.screen) && !bindings.hidden?.has(el.id)).map((el) => el.id),
  )
  const now = new Set(shown.map((el) => el.id))
  const all = drawOrder(doc.elements).filter((el) => was.has(el.id) || now.has(el.id))
  const order = enterOrder(all)
  return (
    <>
      {all.map((el) => {
        const box = boxStyle(el)
        const step = { ['--flip-i' as string]: Math.min(order.get(el.id) ?? 0, FLIP_STEPS) }
        const out = was.has(el.id)
        const into = now.has(el.id)
        if (flip.numberOnly && !out) {
          return (
            <div key={el.id} lang={screenLanguage(bindings)} style={box} data-element={el.id}
            data-ink={elementInk(el.style?.background, doc.canvas)}>
              <ElementBody element={el} bindings={bindings} kind={doc.kind} canvas={doc.canvas} words={doc.words} />
            </div>
          )
        }
        if (el.kind === 'ticketNumber') {
          const numberFlip = { from: out ? (from.ticketNumber ?? '') : '', all: flip.numberOnly }
          const at = { ...bindings, ticketNumber: into ? (bindings.ticketNumber ?? '') : '', numberFlip }
          return (
            <div key={`${el.id}:${flip.serial}`} lang={screenLanguage(bindings)} style={{ ...box, ...step }} data-element={el.id}
            data-ink={elementInk(el.style?.background, doc.canvas)} data-number>
              <ElementBody element={el} bindings={at} kind={doc.kind} canvas={doc.canvas} words={doc.words} />
            </div>
          )
        }
        const { position, left, top, width, height, zIndex, ...look } = box
        const half = (side: string, b: LayoutBindings, hidden: boolean) => (
          <div className={side} style={look} aria-hidden={hidden || undefined}>
            <ElementBody element={el} bindings={b} kind={doc.kind} canvas={doc.canvas} words={doc.words} />
          </div>
        )
        return (
          <div
            key={`${el.id}:${flip.serial}`}
            lang={screenLanguage(bindings)}
            className="cf-card"
            style={{ position, left, top, width, height, zIndex, perspective: Math.max(600, el.h * 4), ...step }}
            data-element={el.id}
            data-ink={elementInk(el.style?.background, doc.canvas)}
          >
            {out && half('cf-half cf-out-top', from, true)}
            {out && half('cf-half cf-out-bottom', from, true)}
            {into && half('cf-half cf-in-top', bindings, true)}
            {into && half('cf-half cf-in-bottom', bindings, false)}
          </div>
        )
      })}
    </>
  )
}

function boxStyle(el: LayoutElement): CSSProperties {
  const style = el.style ?? {}
  return {
    position: 'absolute',
    left: el.x,
    top: el.y,
    width: el.w,
    height: el.h,
    zIndex: el.z ?? 0,
    background: style.background ? colour(style.background, 'transparent') : undefined,
    borderRadius: style.radius ? style.radius : undefined,
    border: style.border ? `${style.borderWidth ?? 2}px solid ${colour(style.border, 'transparent')}` : undefined,
    boxShadow: style.shadow ? '0 6px 18px rgb(0 0 0 / 0.16), 0 1px 3px rgb(0 0 0 / 0.12)' : undefined,
    color: colour(style.colour, 'var(--ink-body)'),
    fontFamily: elementFontFamily(el),
    fontWeight: style.weight ?? undefined,
    textAlign: style.align ?? 'left',
    textTransform: style.uppercase ? 'uppercase' : undefined,
    letterSpacing: style.uppercase ? '0.12em' : undefined,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
  }
}

/** An element's own font over the design's; "standard" undoes a design font. */
export function elementFontFamily(el: Pick<LayoutElement, 'font'>): string | undefined {
  if (!el.font) return undefined
  return el.font === STANDARD_FONT ? 'var(--font-body)' : fontStack(el.font)
}

/** How big text in a box should be, given the box and the element's own scale. */
function fontSize(el: LayoutElement, share: number): number {
  return Math.max(10, Math.round(el.h * share * (el.style?.scale ?? 1)))
}

/**
 * How much of its box a line of text takes.
 *
 * A printed ticket is set at the share cmd/qms-agent uses, and the screens keep the
 * shares they have always had. That difference is the point rather than an
 * oversight: a ticket preview is a picture of what another program will typeset, so
 * the only number worth using is that program's, while a hall board's preview is a
 * picture of this very renderer and changing it would change what is on the wall.
 */
const TEXT_SHARE = 0.5
const BRANCH_SHARE = 0.6
const PAPER_SHARE = 0.62

/** The line spacing of everything measured here, and of everything drawn here. */
const LINE_HEIGHT = 1.15

function alignItems(el: LayoutElement): CSSProperties['alignItems'] {
  switch (el.style?.align) {
    case 'center':
      return 'center'
    case 'right':
      return 'flex-end'
    default:
      return 'flex-start'
  }
}

function ElementBody({
  element: el,
  bindings,
  kind,
  canvas,
  words,
}: {
  element: LayoutElement
  bindings: LayoutBindings
  kind: LayoutDocument['kind']
  canvas: LayoutCanvas
  /** The design's own built-in words (layout/words.ts). */
  words?: Words
}) {
  const lang = bindings.language ?? bindings.languages?.[0]?.code ?? 'en'
  const orgDefault = bindings.languages?.[0]?.code
  const paper = kind === 'ticket'
  // A desk label of the designer's own is drawn with the sample desk, not as a chip.
  const keyword = el.kind === 'deskName' && !plainDeskLabel(words, lang) ? undefined : bindings.keyword?.(el.kind as ElementKind)
  if (keyword) return <Keyword element={el} font={fontOf(canvas, el)} label={keyword} share={paper ? PAPER_SHARE : BRANCH_SHARE} />

  switch (el.kind as ElementKind) {
    case 'text':
      return (
        <FittedText
          element={el}
          font={fontOf(canvas, el)}
          lang={lang}
          paper={paper}
          share={paper ? PAPER_SHARE : TEXT_SHARE}
          text={
            // A designer's sample stands in for the placeholders, and only while
            // they are designing. Left alone, the editor goes on showing exactly
            // what was typed, braces and all, which is what somebody writing the
            // text wants to see.
            bindings.preview && el.previewText
              ? withPreviewText(textIn(el.content, lang, orgDefault), el.previewText)
              : textIn(el.content, lang, orgDefault)
          }
        />
      )

    case 'branchName':
      return (
        <FittedText
          element={el}
          font={fontOf(canvas, el)}
          lang={lang}
          paper={paper}
          share={paper ? PAPER_SHARE : BRANCH_SHARE}
          text={bindings.branchName ?? ''}
        />
      )

    case 'deskName':
      return (
        <FittedText
          element={el}
          font={fontOf(canvas, el)}
          lang={lang}
          share={BRANCH_SHARE}
          text={deskLabel(words, lang, { name: bindings.deskName, number: bindings.deskNumber })}
        />
      )

    case 'serviceName':
      return paper ? (
        <FittedText element={el} font={fontOf(canvas, el)} lang={lang} paper share={PAPER_SHARE} text={bindings.ticketService ?? ''} />
      ) : (
        <FittedText element={el} font={fontOf(canvas, el)} lang={lang} share={BRANCH_SHARE} text={bindings.serviceName ?? ''} />
      )

    case 'ticketPosition':
    case 'ticketWait': {
      const line = el.kind === 'ticketPosition' ? positionLine(bindings, words) : waitLine(bindings, words)
      return <FittedText element={el} font={fontOf(canvas, el)} lang={lang} paper={paper} share={paper ? PAPER_SHARE : FACTS_SHARE} text={line} />
    }

    case 'deskStatus':
      return <FittedText element={el} font={fontOf(canvas, el)} lang={lang} share={BRANCH_SHARE} text={pillWords(words, bindings.deskStatus, lang)} />

    case 'clock':
      return <Clock element={el} now={bindings.now} />

    case 'dateTime':
      return paper ? (
        <span style={{ fontSize: fontSize(el, 0.6) }}>
          {formatDate(bindings.ticketIssuedAt ?? bindings.now ?? new Date(), el.options?.format)}
        </span>
      ) : (
        <ScreenDate element={el} font={fontOf(canvas, el)} lang={lang} now={bindings.now} />
      )

    case 'validUntil':
      return (
        <FittedText
          element={el}
          font={fontOf(canvas, el)}
          lang={lang}
          paper
          share={PAPER_SHARE}
          text={bindings.ticketValidUntil ? validUntilWords(words, bindings.ticketValidUntil, lang) : ''}
        />
      )

    case 'waitingTotal':
      return (
        <span className="num" style={{ fontSize: fontSize(el, 0.6) }}>
          {inQueueWords(words, waitingTotal(bindings.queues), lang)}
        </span>
      )

    case 'rule': {
      // A box taller than it is wide draws a vertical line.
      const upright = el.h > el.w
      const thick = ruleThickness(el, paper)
      const paint = el.style?.gradient ? 'var(--gradient-accent)' : colour(el.style?.colour, 'var(--border-strong)')
      return upright ? (
        <div style={{ width: thick, height: '100%', alignSelf: 'center', background: paint }} />
      ) : (
        <div style={{ width: '100%', height: thick, background: paint }} />
      )
    }

    // Fill, border, corners and shadow are the box's own (boxStyle).
    case 'panel':
      return null

    case 'deskNumber':
      return (
        <span className="num" style={{ fontSize: fontSize(el, 0.86), lineHeight: 1 }}>
          {bindings.deskNumber ?? ''}
        </span>
      )

    case 'statusPill':
      return bindings.deskPill ? <StatusPill element={el} state={bindings.deskPill} lang={lang} words={words} /> : null

    case 'clerkName':
      return bindings.clerkName ? (
        <span className="counter-clerk" style={{ fontSize: fontSize(el, 0.62), alignSelf: alignItems(el) }}>
          <PersonGlyph />
          {bindings.clerkName}
        </span>
      ) : null

    case 'image':
      return bindings.assetURL && el.assetId ? (
        <img
          src={bindings.assetURL(el.assetId)}
          alt=""
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            alignSelf: alignItems(el),
          }}
        />
      ) : (
        // In the editor, before a picture is chosen. On a screen this element
        // cannot exist — the server refuses an image with no picture.
        <span className="layout-placeholder" style={{ fontSize: fontSize(el, 0.2) }}>
          {staffSay('designs.el.image', phraseLanguage(bindings.language)).toLowerCase()}
        </span>
      )

    case 'ticketNumber':
      return (
        <TicketNumber element={el} font={fontOf(canvas, el)} text={bindings.ticketNumber ?? ''}>
          {kind === 'counter' ? <NumberFace number={bindings.ticketNumber ?? ''} flip={bindings.numberFlip} /> : (bindings.ticketNumber ?? '')}
        </TicketNumber>
      )

    case 'ticketFacts':
      return <TicketFacts element={el} bindings={bindings} words={words} />

    case 'calls':
      return <Calls element={el} bindings={bindings} font={fontOf(canvas, el)} words={words} />

    case 'queues':
      return <Queues element={el} bindings={bindings} font={fontOf(canvas, el)} />

    case 'services':
      return <Services element={el} bindings={bindings} />

    case 'button':
      return <ButtonBody element={el} bindings={bindings} />

    case 'languages':
      return <Languages element={el} bindings={bindings} />

    case 'qr':
      return <QRBlock element={el} value={bindings.qrValue} language={bindings.language} preview={bindings.preview} />

    case 'media':
      return <MediaPlayer element={el} assetURL={bindings.assetURL} preview={bindings.preview} play={bindings.playing} onSkip={bindings.onMediaSkipped} />

    case 'ticker':
      // Its own lines first, then the playing playlist's.
      return <Ticker element={el} lines={[...tickerLinesOf(textIn(el.content, lang, orgDefault)), ...(bindings.ticker ?? [])]} preview={bindings.preview} />

    case 'logo':
      return <BrandLogo element={el} canvas={canvas} assetURL={bindings.assetURL} preview={bindings.preview} lang={bindings.language} />

    default:
      return null
  }
}

/** A logo element: its own picture, else the design's brand logo; empty, a placeholder
 *  in the editor and nothing on a screen. */
function BrandLogo({
  element: el,
  canvas,
  assetURL,
  preview,
  lang,
}: {
  element: LayoutElement
  canvas: LayoutCanvas
  assetURL?: (id: string) => string
  preview?: boolean
  lang?: string
}) {
  if (el.assetId && assetURL) {
    return <img src={assetURL(el.assetId)} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', alignSelf: alignItems(el) }} />
  }
  if (logoSource(canvas) !== 'none') return <BrandMark canvas={canvas} assetURL={assetURL} align={alignItems(el)} />
  return preview ? (
    <span className="layout-placeholder" style={{ fontSize: fontSize(el, 0.36), alignSelf: alignItems(el) }}>
      {staffSay('designs.el.logo', phraseLanguage(lang)).toLowerCase()}
    </span>
  ) : null
}

/** The place in line, as the slip prints it (internal/slip positionLine). */
export function positionLine(bindings: LayoutBindings, words?: Words): string {
  return bindings.ticketAhead === undefined ? '' : aheadLine(words, bindings.ticketAhead, screenLanguage(bindings))
}

/** The estimated wait, as the slip prints it (internal/slip waitLine). */
export function waitLine(bindings: LayoutBindings, words?: Words): string {
  return bindings.ticketWait ? waitWords(words, bindings.ticketWait, screenLanguage(bindings)) : ''
}

/** The logo a design names, outside any element: the kiosk's closed screen draws it too. */
export function BrandMark({ canvas, assetURL, align = 'center' }: { canvas: LayoutCanvas; assetURL?: (id: string) => string; align?: CSSProperties['alignItems'] }) {
  const source = logoSource(canvas)
  if (source === 'none') return null
  const own = source === 'organisation' ? canvas.logo?.assetId : undefined
  if (own) {
    return assetURL ? <img src={assetURL(own)} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', alignSelf: align }} /> : null
  }
  return (
    <span
      className="layout-logo"
      role="img"
      aria-label={staffSay('designs.look.qmeter', 'en')}
      style={{ '--logo': `url(${qmeterLogo})`, maskPosition: align === 'flex-end' ? 'right' : align === 'center' ? 'center' : 'left' } as CSSProperties}
    />
  )
}

/** The person glyph a counter display's pill and clerk carry, in the text colour. */
function PersonGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="counter-glyph"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: kioskIcon('user') ?? '' }}
    />
  )
}

/** The number a flip turns over from; `all` turns every character, as a recall does. */
export interface NumberFlip {
  from: string
  all: boolean
}

/** A counter display's number, a box per character so a flip can turn each in turn.
 *  In a flip-clock change only the characters that differ turn over, each a split flap. */
function NumberFace({ number, flip }: { number: string; flip?: NumberFlip }) {
  if (!flip) {
    return (
      <span className="num-face">
        {Array.from(number).map((ch, i) => (
          <span key={i} className="num-ch" style={{ ['--i' as string]: i }}>
            {ch}
          </span>
        ))}
      </span>
    )
  }
  const was = Array.from(flip.from)
  const now = Array.from(number)
  return (
    <span className="num-face">
      {Array.from({ length: Math.max(was.length, now.length) }, (_, i) => {
        const a = was[i] ?? ''
        const b = now[i] ?? ''
        if (a === b && !flip.all) {
          return (
            <span key={i} className="num-ch">
              {b}
            </span>
          )
        }
        return (
          <span key={i} className="num-ch cf-ch" style={{ ['--i' as string]: i }}>
            <span className="cf-ghost">{b || a}</span>
            {a && (
              <span className="cf-half cf-out-top" aria-hidden>
                {a}
              </span>
            )}
            {a && (
              <span className="cf-half cf-out-bottom" aria-hidden>
                {a}
              </span>
            )}
            {b && (
              <span className="cf-half cf-in-top" aria-hidden>
                {b}
              </span>
            )}
            {b && <span className="cf-half cf-in-bottom">{b}</span>}
          </span>
        )
      })}
    </span>
  )
}

/** A slow drift of the brand's light behind a kiosk's welcome screen; still when motion is reduced. */
export function Ambient() {
  return (
    <div className="layout-ambient" aria-hidden="true">
      <span className="layout-ambient__a" />
      <span className="layout-ambient__b" />
    </div>
  )
}

/** A counter display's status pill, its words shrunk to keep the whole pill inside its box. */
function StatusPill({ element: el, state, lang, words }: { element: LayoutElement; state: DeskPill; lang: string; words?: Words }) {
  const text = pillWords(words, state, lang)
  const start = fontSize(el, 0.42)
  const weight = el.style?.weight ?? 400
  const fontsReady = useFontsReady()
  const size = useMemo(
    () => pillSize(text, el.w, start, ruler(weight, false, lang)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text, el.w, start, weight, lang, fontsReady],
  )
  return (
    <span className="counter-pill" data-state={state} style={{ fontSize: size, alignSelf: alignItems(el) }}>
      <PersonGlyph />
      {text}
    </span>
  )
}

/** A chip's text against the size the field's value would have. */
const KEYWORD_SCALE = 0.85

/** A live field's name as a chip, one line, shrunk by its drawn width to fit its box. */
function Keyword({ element: el, font, label, share }: { element: LayoutElement; font: string | undefined; label: string; share: number }) {
  const ref = useRef<HTMLSpanElement>(null)
  const fontsReady = useFontsReady()
  const fontLoaded = useFontLoaded(font)
  const start = Math.max(8, Math.min(Math.round(fontSize(el, share) * KEYWORD_SCALE), Math.floor(el.h / 1.6)))
  const [size, setSize] = useState(start)
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const measure = () => {
      // Measured at its natural width: the box would otherwise squeeze it to its own.
      node.style.fontSize = start + 'px'
      node.style.maxWidth = 'none'
      node.style.width = 'max-content'
      const drawn = node.offsetWidth
      node.style.maxWidth = ''
      node.style.width = ''
      const room = el.w * 0.94
      const next = drawn > room ? Math.max(8, Math.floor((start * room) / drawn)) : start
      node.style.fontSize = next + 'px'
      setSize(next)
    }
    measure()
    // The chip's weight may load after it is first drawn.
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined
    fonts?.addEventListener('loadingdone', measure)
    return () => fonts?.removeEventListener('loadingdone', measure)
  }, [start, label, el.w, fontsReady, fontLoaded])
  return (
    <span ref={ref} className="layout-keyword" style={{ fontSize: size, alignSelf: alignItems(el) }}>
      {label}
    </span>
  )
}

/**
 * A line of text, set to the rows its design allowed it.
 *
 * With no row limit this is exactly what it always was: one span at the size the
 * box implies, wrapped and clipped by the browser. Every design drawn before row
 * limits existed goes through that path and comes out unchanged.
 *
 * With one, the rule in fit.ts decides — the same rule cmd/qms-agent runs against
 * the real paper. The point of the feature is that a designer sees the overflow
 * before the customer does, so the canvas has to do the shrinking and the cropping
 * rather than merely report them: a preview that quietly fits what the printer will
 * cut is the bug this whole change exists to remove, wearing a different hat.
 */
function FittedText({
  element: el,
  font,
  text,
  share,
  lang,
  paper = false,
}: {
  element: LayoutElement
  /** The chosen font it is drawn in; undefined is the standard face. */
  font: string | undefined
  text: string
  share: number
  lang: string
  /** A printed ticket: auto-fit only where the slip renderer does it too (text, branch name). */
  paper?: boolean
}) {
  const rows = el.options?.maxRows ?? 0
  const start = fontSize(el, share)
  const weight = el.style?.weight ?? 400
  const uppercase = el.style?.uppercase ?? false

  const fontsReady = useFontsReady()
  const fontLoaded = useFontLoaded(font)
  const autofit = !paper || fitsRows(el.kind)
  const minScale = el.options?.minScale
  const fitted = useMemo(
    () => fitElementText(text, rows, el.w, el.h, start, minScale, autofit, paper, ruler(weight, uppercase, lang, font)),
    // fontsReady: measured before the face has loaded, a line is broken for the
    // fallback face and drawn in the real one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text, rows, el.w, el.h, start, minScale, autofit, paper, weight, uppercase, lang, font, fontsReady, fontLoaded],
  )

  if (!fitted) {
    return <span style={{ fontSize: start, lineHeight: LINE_HEIGHT }}>{text}</span>
  }
  // pre, because the lines have already been broken where the rule broke them.
  // Letting the browser wrap them again would be a second opinion on the one
  // question this element exists to settle.
  return (
    <span style={{ fontSize: fitted.size, lineHeight: LINE_HEIGHT, whiteSpace: 'pre' }}>
      {fitted.lines.join('\n')}
    </span>
  )
}

/** The fit rule an element's text is set by: its row limit, else auto-fit, else none (null). */
function fitElementText(
  text: string,
  rows: number,
  w: number,
  h: number,
  start: number,
  minScale: number | undefined,
  autofit: boolean,
  paper: boolean,
  m: Measure,
): Fitted | null {
  if (rows >= 1) return fitText(text, rows, w, h, start, m)
  if (!autofit) return null
  return autoFit(text, w, h, start, minScaleOf(minScale), paper ? { ...m, floor: MIN_FIT_SIZE } : m)
}

/**
 * The customer languages whose words do not fit a text element's box even at its
 * smallest size: what the editor marks "too long". A language with no words of its
 * own falls back and is not counted.
 */
export function overflowingLanguages(el: LayoutElement, doc: LayoutDocument, languages: readonly string[]): string[] {
  if (el.kind !== 'text' || el.hidden) return []
  const paper = doc.kind === 'ticket'
  const start = fontSize(el, paper ? PAPER_SHARE : TEXT_SHARE)
  const font = fontOf(doc.canvas, el)
  const out: string[] = []
  for (const lang of languages) {
    const own = el.content?.[lang]?.trim()
    if (!own) continue
    const text = el.previewText ? withPreviewText(own, el.previewText) : own
    const m = ruler(el.style?.weight ?? 400, el.style?.uppercase ?? false, lang, font)
    const fitted = fitElementText(text, el.options?.maxRows ?? 0, el.w, el.h, start, el.options?.minScale, true, paper, m)
    if (fitted?.cropped) out.push(lang)
  }
  return out
}

/**
 * Measuring text the way this browser is about to draw it.
 *
 * A canvas rather than a hidden element in the document: measuring by layout means
 * writing into the page and reading it back, which is a reflow per attempt and a
 * ladder of attempts per element. The font is the one the design system sets, read
 * from the token rather than named here, so this measures what is actually on
 * screen and not what a constant in this file believes is.
 *
 * It will not agree with DejaVu on a printer to the dot, and it is not supposed to.
 * What it agrees on is the rule: whether the text has to shrink, how far it is
 * allowed to, how many rows it gets and that the rest is cut.
 */
function ruler(weight: number, uppercase: boolean, lang: string, font?: string): Measure {
  return {
    width(text, size) {
      // Capitals are what is drawn, and the design system spaces them out — both
      // make the line longer, and a preview that measured the lower-case original
      // would under-report the overflow it exists to find.
      const shown = uppercase ? text.toLocaleUpperCase(lang) : text
      const spacing = uppercase ? Math.max(Array.from(shown).length - 1, 0) * size * UPPERCASE_SPACING : 0
      const context = rulerContext()
      if (!context) return Array.from(shown).length * size * AVERAGE_GLYPH + spacing
      context.font = [weight, size + 'px', rulerFamily(font)].join(' ')
      return context.measureText(shown).width + spacing
    },
    height: (size) => size * LINE_HEIGHT,
  }
}

/** The letter spacing boxStyle adds to capitals, as a share of the size. */
const UPPERCASE_SPACING = 0.12

/** What a character is assumed to be worth where there is no canvas to ask — a
 *  test runner, or a browser that refuses the context. Roughly right for a
 *  proportional face at this size, and only ever used where the alternative is
 *  measuring nothing at all. */
const AVERAGE_GLYPH = 0.52

let sharedRuler: CanvasRenderingContext2D | null | undefined
function rulerContext(): CanvasRenderingContext2D | null {
  if (sharedRuler === undefined) {
    try {
      sharedRuler = document.createElement('canvas').getContext('2d')
    } catch {
      sharedRuler = null
    }
  }
  return sharedRuler
}

/** Whether the page's fonts have finished loading; true at once where they have. */
function useFontsReady(): boolean {
  const fonts = typeof document !== 'undefined' ? document.fonts : undefined
  const [ready, setReady] = useState(() => !fonts || fonts.status === 'loaded')
  useEffect(() => {
    if (ready || !fonts) return
    let live = true
    void fonts.ready.then(() => live && setReady(true))
    return () => {
      live = false
    }
  }, [ready, fonts])
  return ready
}

/** The family the ruler measures in: the chosen font over the body face. */
function rulerFamily(font?: string): string {
  return font ? `"qf-${font}", ${bodyFont()}` : bodyFont()
}

let sharedFont: string | undefined
/**
 * The standard face as the canvas resolves it (.layout-canvas draws in --font-body),
 * not the page's: measuring in another face broke lines for one font and drew them
 * in another (found 2 October 2026). The token, because a design font sits on the canvas.
 */
function bodyFont(): string {
  if (sharedFont === undefined) {
    let family = ''
    try {
      const drawn = document.querySelector('.layout-canvas')
      family = drawn ? getComputedStyle(drawn).getPropertyValue('--font-body').trim() : ''
      if (!family && document.body) family = getComputedStyle(document.body).fontFamily.trim()
    } catch {
      family = ''
    }
    // Not kept until a canvas or body has said: a family read before either
    // exists would be the default and would stay wrong for the life of the page.
    if (!family) return 'sans-serif'
    sharedFont = family
  }
  return sharedFont
}

function Clock({ element: el, now }: { element: LayoutElement; now?: Date }) {
  // Ticks on its own so the rest of the board does not repaint every second.
  const [, force] = useState(0)
  useEffect(() => {
    if (now) return
    const timer = window.setInterval(() => force((n) => n + 1), 1000)
    return () => window.clearInterval(timer)
  }, [now])

  const at = now ?? new Date()
  return (
    <span className="num" style={{ fontSize: fontSize(el, 0.7) }}>
      {at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </span>
  )
}

/** A screen's Date or Time: now, in the screen's language, ticking on its own. */
function ScreenDate({ element: el, font, lang, now }: { element: LayoutElement; font: string | undefined; lang: string; now?: Date }) {
  const [, force] = useState(0)
  useEffect(() => {
    if (now) return
    const timer = window.setInterval(() => force((n) => n + 1), 1000)
    return () => window.clearInterval(timer)
  }, [now])
  return <FittedText element={el} font={font} lang={lang} share={0.6} text={formatDate(now ?? new Date(), el.options?.format, lang)} />
}

/**
 * The share of its box one line of the facts takes. Sized for all three lines —
 * service, people ahead, wait — whichever are present, so the text does not jump
 * when a ticket has no estimate. It was 0.28 when there were two lines; the
 * service line made three, which no longer fitted and was cut at top and bottom.
 */
const FACTS_SHARE = 0.95 / (3 * LINE_HEIGHT + 2 * 0.3)

function TicketFacts({ element: el, bindings, words }: { element: LayoutElement; bindings: LayoutBindings; words?: Words }) {
  const size = fontSize(el, FACTS_SHARE)
  // In the language the customer chose. These lines were once Azerbaijani
  // whatever was chosen, which made them the one part of the issued screen a
  // customer who had just picked Russian or English might not read.
  const lang = screenLanguage(bindings)
  return (
    <div style={{ display: 'grid', gap: size * 0.3, fontSize: size, lineHeight: LINE_HEIGHT }}>
      {/* What they are waiting for comes first: it is the fact that decides
          whether they are standing in the right queue at all. */}
      {bindings.ticketService && <span>{bindings.ticketService}</span>}
      {bindings.ticketAhead !== undefined && <span>{aheadLine(words, bindings.ticketAhead, lang)}</span>}
      {bindings.ticketWait && <span>{waitWords(words, bindings.ticketWait, lang)}</span>}
    </div>
  )
}

/** A design's own "No calls" says it for a board of some desks too. */
function noCallsLine(words: Words, bindings: LayoutBindings): string {
  const lang = bindings.language ?? 'en'
  return ownWord(words, 'boardNoCalls', lang) ?? bindings.callsEmptyText ?? wordIn(words, 'boardNoCalls', lang)
}

/** The smallest the empty line of a list of calls is drawn, in canvas units. */
const CALLS_EMPTY_FLOOR = 28

/**
 * How large a list of calls says that nobody has been called.
 *
 * From its box, like every other line here, but not below a floor: a short column
 * (the "Before this" of Split with a playlist, the strip under Headline) would
 * otherwise say it at a size nobody across a hall can read, and the sentence is
 * the whole of what such a board shows on a quiet morning. The floor is about Split's own
 * column's size, so a design that already read well is unchanged. It never takes
 * more than half of a box too small for it, so it cannot spill out of one.
 */
export function callsEmptySize(el: LayoutElement): number {
  // Never below the 10 every other line keeps, however short the box.
  return Math.max(Math.min(Math.max(fontSize(el, 0.08), CALLS_EMPTY_FLOOR), Math.round(el.h / 2)), 10)
}

function Calls({ element: el, bindings, font, words }: { element: LayoutElement; bindings: LayoutBindings; font: string | undefined; words?: Words }) {
  const columns = el.options?.columns ?? 3
  const rows = el.options?.rows ?? 2
  // Skip is what lets two elements read the same list and show different parts
  // of it: the number being called now, and the ones before it.
  const skip = Math.max(el.options?.skip ?? 0, 0)
  const calls = (bindings.calls ?? []).slice(skip, skip + columns * rows)
  const lang = screenLanguage(bindings)

  // A list that skips the first (Previous) stays blank: the current call, or the
  // list without a skip, already says nobody has been called.
  if (calls.length === 0 && skip > 0) return null
  if (calls.length === 0) {
    // Empty unless the organisation or the design wrote a "No calls" line.
    const line = noCallsLine(words, bindings)
    return line ? (
      <span className="layout-placeholder" style={{ fontSize: callsEmptySize(el), textAlign: 'center' }}>
        {line}
      </span>
    ) : null
  }

  // The cell sizes itself from the grid rather than from the number of calls, so a
  // board with two people on it and a board with six read at the same size. A
  // number that grows when the hall is quiet is a number nobody trusts.
  const cellHeight = el.h / rows
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridAutoRows: `${cellHeight}px`,
        width: '100%',
        height: '100%',
      }}
    >
      {calls.map((call) => (
        <div
          key={call.id}
          className="layout-call"
          // Centred unless the designer said otherwise, as a call cell always was.
          style={{ alignItems: el.style?.align ? alignItems(el) : 'center' }}
        >
          <CallNumber element={el} font={font} text={call.number} size={cellHeight * 0.52 * (el.style?.scale ?? 1)} room={(el.w / columns) * NUMBER_ROOM} />
          {el.options?.showDesk !== false && (
            <span style={{ fontSize: cellHeight * 0.16, opacity: 0.75 }}>{deskLabel(words, lang, { name: call.desk, number: call.deskNumber })}</span>
          )}
        </div>
      ))}
    </div>
  )
}

/** The share of its box a number may take across before a chosen face is set smaller. */
const NUMBER_ROOM = 0.86

/**
 * A number's size in a chosen font: as asked, or smaller where that face is too wide
 * for its room. The standard face is left as it always was.
 */
function useNumberSize(el: LayoutElement, font: string | undefined, text: string, size: number, room: number): number {
  const loaded = useFontLoaded(font)
  const weight = el.style?.weight ?? 400
  return useMemo(() => {
    if (!font || !text || !rulerContext()) return size
    const width = ruler(weight, false, 'en', font).width(text, size)
    return width > room ? Math.floor((size * room) / width) : size
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [font, text, size, room, weight, loaded])
}

function TicketNumber({ element: el, font, text, children }: { element: LayoutElement; font: string | undefined; text: string; children: ReactNode }) {
  const size = useNumberSize(el, font, text, fontSize(el, 0.78), el.w * NUMBER_ROOM)
  return (
    <span className="num" style={{ fontSize: size, lineHeight: 1, whiteSpace: font ? 'nowrap' : undefined }}>
      {children}
    </span>
  )
}

function CallNumber({ element: el, font, text, size, room }: { element: LayoutElement; font: string | undefined; text: string; size: number; room: number }) {
  const fitted = useNumberSize(el, font, text, size, room)
  return (
    <span className="num" style={{ fontSize: fitted, lineHeight: 1, whiteSpace: font ? 'nowrap' : undefined }}>
      {text}
    </span>
  )
}

/** Everyone holding a waiting ticket in the branch: the sum of the floor's queue
 *  counts, which already leave out the called and the set aside. */
export function waitingTotal(queues?: readonly { waiting: number }[]): number {
  return (queues ?? []).reduce((sum, q) => sum + q.waiting, 0)
}

function Queues({ element: el, bindings, font }: { element: LayoutElement; bindings: LayoutBindings; font: string | undefined }) {
  if (el.options?.view === 'grid') return <QueueGrid element={el} bindings={bindings} font={font} />
  const size = fontSize(el, 0.42)
  return (
    <div style={{ display: 'flex', gap: size * 1.6, flexWrap: 'wrap', fontSize: size, justifyContent: alignItems(el) }}>
      {(bindings.queues ?? []).map((q) => (
        <span key={q.id} className="num">
          {q.name}: {q.waiting}
        </span>
      ))}
    </div>
  )
}

/** A column per queue, its waiting numbers under its name; the box decides how many rows. */
function QueueGrid({ element: el, bindings, font }: { element: LayoutElement; bindings: LayoutBindings; font: string | undefined }) {
  const queues = bindings.queues ?? []
  const { row, rows } = gridRows(el.h, el.style?.scale ?? 1)
  const columnWidth = queues.length > 0 ? el.w / queues.length : el.w
  const columns = queues.map((q) => ({ ...q, ...gridColumn(q.tickets ?? [], rows) }))
  const weight = el.style?.weight ?? 400
  const uppercase = el.style?.uppercase ?? false
  const lang = screenLanguage(bindings)
  const fontsReady = useFontsReady()
  const fontLoaded = useFontLoaded(font)
  const cells = columns.flatMap((c) => (c.more > 0 ? [...c.shown, `+${c.more}`] : c.shown))
  // One size for every number, the widest fitted to its column.
  const numberSize = useMemo(() => {
    const start = row * 0.6
    const room = columnWidth * NUMBER_ROOM
    const m = ruler(weight, false, 'en', font)
    const widest = Math.max(0, ...cells.map((text) => m.width(text, start)))
    return widest > room ? Math.max(Math.floor((start * room) / widest), 6) : start
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells.join(' '), row, columnWidth, weight, font, fontsReady, fontLoaded])
  const align = el.style?.align ?? 'center'
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(queues.length, 1)}, 1fr)`, width: '100%', height: '100%', textAlign: align }}>
      {columns.map((c) => {
        const head = fitText(c.name, 1, columnWidth * 0.92, row, row * 0.42, { ...ruler(weight, uppercase, lang, font), floor: row * 0.42 * DEFAULT_MIN_SCALE })
        return (
          <div key={c.id} className="layout-queue-col" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span className="layout-queue-col__head" style={{ height: row, lineHeight: row / head.size, fontSize: head.size, whiteSpace: 'pre' }}>
              {head.lines.join('') || c.name}
            </span>
            {c.shown.map((n) => (
              <span key={n} className="num" style={{ height: row, lineHeight: row / numberSize, fontSize: numberSize, whiteSpace: 'nowrap' }}>
                {n}
              </span>
            ))}
            {c.more > 0 && (
              <span className="num layout-queue-col__more" style={{ height: row, lineHeight: row / (numberSize * 0.8), fontSize: numberSize * 0.8 }}>
                +{c.more}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

function Services({ element: el, bindings }: { element: LayoutElement; bindings: LayoutBindings }) {
  const columns = el.options?.columns ?? 2
  const services = bindings.servicesFor?.(el) ?? bindings.services ?? []

  // A branch with six services and a box designed for fourteen should not have its
  // buttons huddled at the top — and a branch with twenty must not have the last
  // six clipped off the bottom. So: centre them when they fit, and start from the
  // top when they do not, where at least the ones that show are the first ones.
  const rows = Math.ceil(services.length / columns)
  const needed = rows * SERVICE_ROW + Math.max(0, rows - 1) * SERVICE_GAP
  const fits = needed <= el.h

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridAutoRows: `${SERVICE_ROW}px`,
        gap: SERVICE_GAP,
        width: '100%',
        height: '100%',
        alignContent: fits ? 'center' : 'start',
        overflow: 'hidden',
      }}
    >
      {services.map((service) => (
        <button
          key={service.id}
          className="layout-service"
          style={choiceStyle(el, 30, 'flex-start')}
          onClick={() => bindings.onPickService?.(service.id)}
        >
          {el.options?.showPrefix !== false && service.prefix && <span className="layout-service__prefix num">{service.prefix}</span>}
          <span className="layout-service__name">{service.name}</span>
        </button>
      ))}
    </div>
  )
}

/**
 * A language or service button drawn as its element says: colour, weight, size and
 * where its words sit. These had fixed styles, so the inspector's settings for them
 * changed nothing (found in a browser pass on 1 October 2026).
 */
function choiceStyle(el: LayoutElement, base: number, defaultJustify: CSSProperties['justifyContent']): CSSProperties {
  const style = el.style ?? {}
  return {
    color: style.colour ? colour(style.colour, 'var(--ink-strong)') : undefined,
    fontWeight: style.weight ?? undefined,
    fontSize: Math.round(base * (style.scale ?? 1)),
    justifyContent: style.align ? (alignItems(el) as CSSProperties['justifyContent']) : defaultJustify,
    textAlign: style.align ?? undefined,
  }
}

/** A language switch: a button per customer language other than the one showing, labelled by code. */
function Languages({ element: el, bindings }: { element: LayoutElement; bindings: LayoutBindings }) {
  const codes = otherLanguages((bindings.languages ?? []).map((l) => l.code), screenLanguage(bindings))
  if (codes.length === 0) {
    // One language needs no choice; the editor still shows where the switch stands.
    return bindings.preview ? (
      <span className="layout-placeholder" style={{ fontSize: fontSize(el, 0.32), alignSelf: alignItems(el) }}>
        {staffSay('designs.el.languages', phraseLanguage(bindings.language)).toLowerCase()}
      </span>
    ) : null
  }
  const style = el.style ?? {}
  // Right unless aligned otherwise: it stands in a corner.
  const justify = style.align ? (alignItems(el) as CSSProperties['justifyContent']) : 'flex-end'
  return (
    <div className="layout-langswitch" style={{ justifyContent: justify, gap: Math.round(el.h * 0.22) }}>
      {codes.map((code) => (
        <button
          key={code}
          type="button"
          className="layout-language"
          style={{
            minWidth: el.h,
            fontSize: fontSize(el, 0.4),
            color: style.colour ? colour(style.colour, 'var(--ink-strong)') : undefined,
            fontWeight: style.weight ?? 600,
          }}
          onClick={(e) => {
            // The welcome screen's own touch would also fire; the switch has done its own thing.
            e.stopPropagation()
            bindings.onPickLanguage?.(code)
          }}
        >
          {switchLabel(code)}
        </button>
      ))}
    </div>
  )
}

/**
 * The QR block.
 *
 * Drawn as a placeholder rather than as a real code, deliberately. A printed
 * ticket's QR is rendered by the agent against the real printer's dots, and a
 * browser-drawn approximation here would be a second implementation that disagrees
 * with it. What this shows is where the code will be and how big.
 */
function QRBlock({ element: el, value, language, preview }: { element: LayoutElement; value?: string; language?: string; preview?: boolean }) {
  // A designer sees a real, scannable sample; a screen keeps the placeholder.
  if (preview) {
    return (
      <svg viewBox={`0 0 ${SAMPLE_QR_SIZE} ${SAMPLE_QR_SIZE}`} width="100%" height="100%" shapeRendering="crispEdges" aria-hidden="true">
        <path d={SAMPLE_QR_PATH} fill={colour(el.style?.colour, '#000000')} />
      </svg>
    )
  }
  return (
    <div
      className="layout-qr"
      style={{ width: '100%', height: '100%' }}
      title={value ?? staffSay('designs.ed.qrTitle', phraseLanguage(language))}
      aria-hidden="true"
    >
      <span style={{ fontSize: Math.max(9, el.w * 0.09) }}>QR</span>
    </div>
  )
}

/**
 * One button a designer placed: a service, a page, or the way back.
 *
 * The box is the button — its background, corners, border and shadow are the
 * element's — so what is drawn here is only what is inside: a picture filling it,
 * the title and the second line. A title never set is the service's or page's own name in
 * the customer's language, so renaming a service renames it on every kiosk; one cleared stays blank.
 */
function ButtonBody({ element: el, bindings }: { element: LayoutElement; bindings: LayoutBindings }) {
  const lang = screenLanguage(bindings)
  const action = el.action
  const title =
    buttonWords(el.content, lang) ??
    (action?.kind === 'service'
      ? (bindings.serviceLabel?.(action.serviceId) ?? '')
      : action?.kind === 'page'
        ? (bindings.pageLabel?.(action.pageId) ?? '')
        : action?.kind === 'back'
          ? t('back', lang as Language)
          : action?.kind === 'booking'
            ? t('haveBooking', lang as Language)
            : '')
  const subtitle = buttonWords(el.subtitle, lang) ?? ''
  // A picture fills the button behind the words, which keep their place and size.
  const photo = el.assetId && bindings.assetURL ? bindings.assetURL(el.assetId) : undefined
  const titleSize = fontSize(el, subtitle ? 0.22 : 0.28)
  // The halo is the button's own fill, the colour its words were chosen against.
  const halo = photo ? ({ '--button-halo': colour(el.style?.background, 'var(--scrim)') } as CSSProperties) : undefined

  return (
    <button
      type="button"
      className="layout-button"
      data-photo={photo ? true : undefined}
      style={{ justifyContent: alignItems(el) as CSSProperties['justifyContent'], ...halo }}
      onClick={(e) => {
        // The kiosk's shell turns any touch on the welcome screen into "start";
        // a button has done its own thing and must not also do that.
        e.stopPropagation()
        if (action) bindings.onButton?.(action)
      }}
    >
      {photo && <img src={photo} alt="" className="layout-button__photo" />}
      {(title || subtitle) && (
        <span className="layout-button__words" style={{ textAlign: el.style?.align ?? 'left' }}>
          {title && (
            <span className="layout-button__title" style={{ fontSize: titleSize, fontWeight: el.style?.weight ?? 600 }}>
              {title}
            </span>
          )}
          {subtitle && (
            <span className="layout-button__subtitle" style={{ fontSize: Math.round(titleSize * 0.62) }}>
              {subtitle}
            </span>
          )}
        </span>
      )}
    </button>
  )
}
